import { apiClient } from './api';
import { 
  ColumnPreferencesResponse, 
  ColumnPreferencesUpdate,
  ColumnDefinition 
} from '../types/api';

export class ColumnPreferencesService {
  /**
   * Get user's column preferences for a specific table
   */
  static async getColumnPreferences(tableName: string): Promise<ColumnPreferencesResponse> {
    try {
      const response = await apiClient.get(`/user/column-preferences/?table=${tableName}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch column preferences:', error);
      return {}; // Return empty object as fallback
    }
  }

  /**
   * Update user's column preferences
   */
  static async updateColumnPreferences(
    tableName: string, 
    preferences: Record<string, string>
  ): Promise<void> {
    const data: ColumnPreferencesUpdate = {
      table_name: tableName,
      preferences
    };

    await apiClient.put('/user/column-preferences/update/', data);
  }

  /**
   * Update a single column preference
   */
  static async updateSingleColumnPreference(
    tableName: string,
    columnKey: string,
    customName: string
  ): Promise<void> {
    await this.updateColumnPreferences(tableName, { [columnKey]: customName });
  }

  /**
   * Reset a single column to default name
   */
  static async resetColumnPreference(tableName: string, columnKey: string): Promise<void> {
    await apiClient.delete(`/user/column-preferences/delete/?table=${tableName}&column=${columnKey}`);
  }

  /**
   * Reset all columns for a table to default names
   */
  static async resetAllColumnPreferences(tableName: string): Promise<void> {
    await apiClient.delete(`/user/column-preferences/reset/?table=${tableName}`);
  }

  /**
   * Merge user preferences with default column definitions
   */
  static mergeWithPreferences(
    defaultColumns: Array<{ key: string; name: string; width?: string }>,
    preferences: ColumnPreferencesResponse
  ): ColumnDefinition[] {
    return defaultColumns.map(col => ({
      key: col.key,
      defaultName: col.name,
      displayName: preferences[col.key] || col.name,
      isCustomized: !!preferences[col.key],
      width: col.width,
      sortable: true
    }));
  }

  /**
   * Get display name for a column (custom or default)
   */
  static getDisplayName(
    columnKey: string, 
    defaultName: string, 
    preferences: ColumnPreferencesResponse
  ): string {
    return preferences[columnKey] || defaultName;
  }

  /**
   * Check if a column has been customized
   */
  static isColumnCustomized(columnKey: string, preferences: ColumnPreferencesResponse): boolean {
    return !!preferences[columnKey];
  }

  /**
   * Validate custom column name
   */
  static validateColumnName(name: string): { isValid: boolean; error?: string } {
    if (!name || !name.trim()) {
      return { isValid: false, error: 'Column name cannot be empty' };
    }

    if (name.length > 200) {
      return { isValid: false, error: 'Column name is too long (max 200 characters)' };
    }

    // Check for potentially problematic characters
    if (name.includes('<') || name.includes('>')) {
      return { isValid: false, error: 'Column name cannot contain < or > characters' };
    }

    return { isValid: true };
  }

  /**
   * Check for duplicate column names
   */
  static hasDuplicateNames(
    columns: ColumnDefinition[], 
    newName: string, 
    excludeKey?: string
  ): boolean {
    return columns.some(col => 
      col.key !== excludeKey && 
      col.displayName.toLowerCase() === newName.toLowerCase()
    );
  }

  /**
   * Export column preferences as JSON
   */
  static exportPreferences(preferences: ColumnPreferencesResponse, tableName: string): string {
    const exportData = {
      tableName,
      preferences,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import column preferences from JSON
   */
  static importPreferences(jsonString: string): { 
    tableName: string; 
    preferences: ColumnPreferencesResponse 
  } | null {
    try {
      const data = JSON.parse(jsonString);
      if (data.tableName && data.preferences) {
        return {
          tableName: data.tableName,
          preferences: data.preferences
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to import preferences:', error);
      return null;
    }
  }
}
